import { useState, useCallback, createContext, useContext } from "react";

export interface ViewListData {
  parent?: ViewListData;
  stepsCount: number;
  activeStep: number;
  nextStep: () => void;
  prevStep: () => void;
  resetStep: () => void;
  goToStep: (step: number) => void;

  initialStep?: number;
  onCompleted?: () => void;
  hasStartStep?: boolean;
  hasEndStep?: boolean;
}

export interface ViewListOptions {
  initialStep?: number;
  onPrevious?: () => void;
  onCompleted?: () => void;
  hasStartStep?: boolean;
  hasEndStep?: boolean;
}

export const ViewListContext = createContext<ViewListData | undefined>(undefined);

/**
 * useLinearFlow - A hook to manage linear flow of steps
 * @returns An object containing activeStep, nextStep, prevStep, resetStep, goToStep
 */
export function useViewList(options: ViewListOptions = {}) {
  const parent = useViewListContext();
  const [activeStep, setActiveStep] = useState(options.initialStep || 0);
  const [stepsCount, setStepsCount] = useState(0);
  const nextStep = useCallback(
    () =>
      setActiveStep((prev) => {
        const step = prev + 1;
        if (step >= stepsCount) {
          if (options.onCompleted) {
            options.onCompleted();
            return prev;
          }
        }
        return step;
      }),
    [stepsCount, setActiveStep],
  );
  const prevStep = useCallback(
    () =>
      setActiveStep((prev) => {
        const step = prev - 1;
        if (step < 0) {
          if (options.onPrevious) {
            options.onPrevious();
            return prev;
          }
        }
        return step;
      }),
    [setActiveStep],
  );
  const resetStep = useCallback(() => setActiveStep(0), []);
  const goToStep = useCallback((step: number) => setActiveStep(step), []);

  return {
    parent,
    stepsCount,
    setStepsCount,
    activeStep,
    nextStep,
    prevStep,
    resetStep,
    goToStep,
    hasStartStep: options.hasStartStep,
    hasEndStep: options.hasEndStep ?? true,
    initialStep: options.initialStep,
    onCompleted: options.onCompleted,
  };
}

export function useViewListContext() {
  const context = useContext(ViewListContext);
  return context;
}
